import React, { useState } from 'react';
import { Button, Icon, Pagination, PaginationProps, Select } from '@gravity-ui/uikit';
import type { MRT_SortingState } from 'material-react-table';
import { AdaptiveBlankBlock } from './blank-block/adaptive-blank-block';
import { Xmark, CaretsExpandVertical } from '@gravity-ui/icons';
import { useBlankActions } from './useBlankActions';
import styles from './styles.module.scss';
import { BlankListFilterSheet } from './blank-list-filter-sheet';
import { useMediaQuery } from '@mui/material';

interface AdaptiveBlankListProps {
	data: Person[];
	refetch: () => void;
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
	pageIndex: number;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	totalItems: number | undefined;
	sorting: MRT_SortingState;
	setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
	role: string | undefined;
}

interface Person {
	id: number | null | undefined;
	firstName: string;
	lastName: string;
	middleName?: string;
	establishment: {
		id?: number;
		name?: string | null;
	} | null;
	createdBlankAt: string | Date;
}

export const AdaptiveBlankList = ({
	data,
	pageIndex,
	setPageIndex,
	pageSize,
	setPageSize,
	sorting,
	setSorting,
	totalItems,
	refetch,
	role,
}: AdaptiveBlankListProps) => {
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [selectedValues, setSelectedValues] = useState<string[]>(['По дате создания']);

	const [visibleFilter, setVisibleFilter] = useState(false);

	const isMobile = useMediaQuery('(max-width: 425px)');

	const { handleDownloadBlank } = useBlankActions(refetch);

	const handleSelect = (id: number, checked: boolean) => {
		setSelectedIds((prev) => {
			if (checked) {
				return [...prev, id];
			} else {
				return prev.filter((val) => val !== id);
			}
		});
	};

	const handleSortChange = (value: string) => {
		switch (value) {
			case 'По дате создания':
				setSorting([{ id: 'createdBlankAt', desc: false }]);
				break;
			case 'От А-Я':
				{
					role === 'SPECIALIST'
						? setSorting([{ id: 'lastName', desc: true }])
						: setSorting([{ id: 'establishment', desc: true }]);
				}

				break;
			case 'От Я-А':
				{
					role === 'SPECIALIST'
						? setSorting([{ id: 'lastName', desc: false }])
						: setSorting([{ id: 'establishment', desc: false }]);
				}
				break;

			case 'Сначала новые':
				setSorting([{ id: 'createdBlankAt', desc: true }]);
				break;
			default:
				setSorting([{ id: 'createdBlankAt', desc: true }]);
				break;
		}
	};
	const label = selectedValues && selectedValues.length > 0 ? selectedValues[0] : 'Не выбрано';

	const handlePaginationUpdate: PaginationProps['onUpdate'] = (newPage, newPageSize) => {
		setPageIndex(newPage - 1);
		setPageSize(newPageSize);
	};

	const closeBanner = () => {
		setSelectedIds([]);
	};

	const onUpdate = (values: string[]) => {
		setSelectedValues(values);

		if (values.length > 0) {
			handleSortChange(values[0]);
		}
	};

	return (
		<div className={styles.list_container}>
			<div className={styles.list_container__blocks}>
				<div>
					{isMobile ? (
						<>
							<Button view="flat" onClick={() => setVisibleFilter(true)}>
								<Icon size={16} data={CaretsExpandVertical} />
								{label}
							</Button>
							<BlankListFilterSheet
								visibleFilter={visibleFilter}
								setVisibleFilter={setVisibleFilter}
								onSortChange={(value: any) => {
									handleSortChange(value);
									setSelectedValues([value]);
								}}
							/>
						</>
					) : (
						<Select
							value={selectedValues}
							onUpdate={onUpdate}
							filterable={true}
							renderControl={({ onClick, onKeyDown, ref }) => {
								return (
									<Button ref={ref} onClick={onClick} extraProps={{ onKeyDown }} view="flat">
										<Icon size={16} data={CaretsExpandVertical} />
										<span>{label}</span>
									</Button>
								);
							}}
						>
							<Select.Option value="По дате создания">По дате создания</Select.Option>
							<Select.Option value="От А-Я">От А-Я</Select.Option>
							<Select.Option value="От Я-А">От Я-А</Select.Option>
							<Select.Option value="Сначала новыe">Сначала новые</Select.Option>
						</Select>
					)}
				</div>

				{data.map((person) => {
					const dateString =
						typeof person.createdBlankAt === 'string'
							? person.createdBlankAt
							: person.createdBlankAt.toLocaleDateString();

					return (
						<AdaptiveBlankBlock
							person={person}
							dateString={dateString}
							selectedIds={selectedIds}
							handleSelect={handleSelect}
							refetch={refetch}
							role={role}
							isMobile={isMobile}
						/>
					);
				})}
			</div>

			<div className={styles.list_container__pagination}>
				{isMobile ? (
					<Pagination
						page={pageIndex + 1}
						pageSize={pageSize}
						total={totalItems}
						onUpdate={handlePaginationUpdate}
					/>
				) : (
					<Pagination
						page={pageIndex + 1}
						pageSize={pageSize}
						total={totalItems}
						pageSizeOptions={[10, 20, 40, 100]}
						onUpdate={handlePaginationUpdate}
					/>
				)}
			</div>

			{selectedIds.length > 0 && (
				<div className={styles.list_container__selected_banner}>
					<div className={styles.list_container__selected_banner__download_block}>
						<p>
							<strong>{selectedIds.length}</strong> бланк
							{selectedIds?.length === 1 ? '' : selectedIds?.length < 5 ? 'а' : 'ов'}
						</p>
						<Button view="normal-contrast" size="l" onClick={() => handleDownloadBlank(selectedIds)}>
							Выгрузить в PDF
						</Button>
					</div>

					<Button view="flat-contrast" size="l" className={styles.list_container__selected_banner__close_btn}>
						<Xmark onClick={closeBanner} />
					</Button>
				</div>
			)}
		</div>
	);
};
